import type { IntegrityCatalogData } from "../../../domain/engineering/integrity-catalog.js";
import { buildIntegrityCatalogSeed } from "../../../domain/engineering/integrity-catalog-seed.js";

const STORAGE_KEY = "integrity_catalogs";

export interface IntegrityCatalogRepository {
  getCatalog(): Promise<IntegrityCatalogData>;
  saveCatalog(data: IntegrityCatalogData): Promise<void>;
  resetToSeed(): Promise<void>;
}

/**
 * Catálogos normativos dos cálculos de integridade.
 *
 * A carga inicial vem do seed; o que a engenharia editar fica guardado e
 * passa a prevalecer. As páginas nunca leem números daqui direto — elas
 * passam pelo IntegrityCatalogService.
 */
export class InMemoryIntegrityCatalogRepository implements IntegrityCatalogRepository {
  private data: IntegrityCatalogData;

  constructor() {
    this.data = buildIntegrityCatalogSeed();
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage === "undefined") return;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const parsed = JSON.parse(stored) as Partial<IntegrityCatalogData>;
      const seed = buildIntegrityCatalogSeed();

      // Uma seção ausente no armazenamento continua vindo do seed — uma
      // gravação parcial antiga não pode apagar catálogo inteiro.
      this.data = {
        materials: parsed.materials ?? seed.materials,
        allowableStressTables: parsed.allowableStressTables ?? seed.allowableStressTables,
        qualityFactors: parsed.qualityFactors ?? seed.qualityFactors,
        weldJointFactors: parsed.weldJointFactors ?? seed.weldJointFactors,
        yCoefficients: parsed.yCoefficients ?? seed.yCoefficients,
        couponMaterials: parsed.couponMaterials ?? seed.couponMaterials,
        editions: parsed.editions ?? seed.editions
      };
    } catch (e) {
      console.warn("Failed to load integrity catalogs from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
      }
    } catch (e) {
      console.warn("Failed to persist integrity catalogs to storage:", e);
    }
  }

  async getCatalog(): Promise<IntegrityCatalogData> {
    this.loadFromStorage();
    return this.data;
  }

  async saveCatalog(data: IntegrityCatalogData): Promise<void> {
    this.data = data;
    this.persistStorage();
  }

  /** Restaura a carga inicial — usado quando a engenharia quer voltar ao ponto de partida. */
  async resetToSeed(): Promise<void> {
    this.data = buildIntegrityCatalogSeed();
    this.persistStorage();
  }
}
